import { DepotError } from '@mesa/core/lib/errors';
import { countRepos, getRepoTagCatalog, listRepos } from '@mesa/core/repo/queries';
import { createServerFn } from '@tanstack/react-start';
import { log } from '../../lib/logger';
import { tagCatalogCache } from '../../lib/repo-tag-catalog-cache';
import { assertScope, baseAuthMiddleware } from '../../middleware/base-auth';

// The label panel selects "key:value" strings. Core takes the same JSON filter
// the REST API accepts: several values for one key become an $in (OR), and
// different keys are AND-ed.
function toTagsFilter(tags: string[] | undefined): string | undefined {
  if (!tags?.length) return undefined;
  const valuesByKey: Record<string, string[]> = Object.create(null);
  for (const tag of tags) {
    const separator = tag.indexOf(':');
    if (separator === -1) {
      throw new DepotError('INVALID_REQUEST', `Label filter '${tag}' must be key:value`);
    }
    (valuesByKey[tag.slice(0, separator)] ??= []).push(tag.slice(separator + 1));
  }
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(valuesByKey).map(([key, values]) => [key, values.length === 1 ? values[0] : values])
    )
  );
}

export const loadRepoListPage = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .validator(
    (data: {
      orgSlug: string;
      tags?: string[];
      search?: string;
      cursor?: string;
      sortBy?: 'name' | 'createdAt';
      sortOrder?: 'asc' | 'desc';
    }) => data
  )
  .handler(async ({ data, context }) => {
    assertScope(context.member.role, 'read');
    const orgId = context.org.id;

    const repoResult = await listRepos(orgId, {
      tags: toTagsFilter(data.tags),
      search: data.search || undefined,
      cursor: data.cursor,
      limit: 10,
      sortBy: data.sortBy,
      sortOrder: data.sortOrder,
    });

    return {
      repos: repoResult.repos,
      nextCursor: repoResult.nextCursor,
      hasMore: repoResult.hasMore,
      tagsByRepo: repoResult.tagsByRepo,
    };
  });

// The count is its own server function, keyed by filters only: it is
// independent of sort and cursor, so Load more clicks and sort toggles must
// not re-walk the org counting rows — at 100K+-repo scale that exact count
// probes hundreds of thousands of rows per call.
export const loadRepoListCount = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string; tags?: string[]; search?: string }) => data)
  .handler(async ({ data, context }) => {
    assertScope(context.member.role, 'read');
    return countRepos(context.org.id, {
      tags: toTagsFilter(data.tags),
      search: data.search || undefined,
    });
  });

// An org's label values can be unbounded (agents stamp repos with ID-valued
// labels), so the filter panel only ever shows the most common values per key.
// The full catalog once grew past Lambda's 6MB response cap and 502'd the
// repositories page for every viewer of that org.
const TAG_CATALOG_MAX_VALUES_PER_KEY = 50;

export const loadRepoTagCatalog = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string }) => data)
  .handler(async ({ context }) => {
    assertScope(context.member.role, 'read');
    const orgId = context.org.id;
    const cached = tagCatalogCache.get(orgId);
    if (cached) return cached;
    try {
      const catalog = await getRepoTagCatalog(orgId, { maxValuesPerKey: TAG_CATALOG_MAX_VALUES_PER_KEY });
      tagCatalogCache.set(orgId, catalog);
      return catalog;
    } catch (err) {
      // Tags are cosmetic: an empty panel beats a broken one. Failures are
      // never cached, so one blip can't blank the panel for the whole TTL.
      log.warn('tag-catalog-failed', { orgId, error: String(err) });
      return {};
    }
  });
